/**
 * Helpers for the STORE payout status (SDC WS6c) and the store owner's Cancel rule.
 *
 * The backend owns every rule and date: the `payout` block on a STORE order (owner views only)
 * carries { status, amount, currencyCode, dueAt, releasedAt, holdReasons }. This file only turns
 * it into what the dashboard shows. A FAILED payout is deliberately shown as still waiting (the
 * server keeps retrying); the owner never sees an error.
 */

/** The payout block of a STORE order, or null (SERVICE orders, orders paid before held payouts). */
export function payoutOf(order) {
  if (order?.orderType !== 'STORE') return null;
  const payout = order.payout;
  return payout && payout.status ? payout : null;
}

const CANCELLED_STATUSES = ['CANCELLED', 'CANCELLED_BY_USER', 'CANCELLED_BY_PROVIDER'];

function isCancelledOrder(order) {
  return CANCELLED_STATUSES.includes(order?.status);
}

/**
 * True when the payout was released before delivery (the payment provider's deadline or an admin
 * release) while the order is still PAID and not yet delivered: the money is already with the
 * store, yet shipping and the buyer's rights continue. Cancelling (only possible before shipping)
 * refunds the buyer in full from the store's balance.
 */
export function isPaidOutEarly(order) {
  const payout = payoutOf(order);
  if (!payout || payout.status !== 'RELEASED' || order.status !== 'PAID') return false;
  const status = order.fulfilment?.status;
  return status === undefined || status === null || status === 'AWAITING_SHIPMENT' || status === 'SHIPPED';
}

/**
 * What to render for a payout:
 *  - key: the `payouts.status<KEY>` label (HELD and FAILED both read "Payout on <date>")
 *  - date: ISO date that fills the label, when it has one
 *  - color: MUI chip colour
 *  - hintKey: the `payouts.hint<KEY>` explanation
 * Returns null when the order has no payout.
 */
export function payoutDescriptor(order) {
  const payout = payoutOf(order);
  if (!payout) return null;

  switch (payout.status) {
    case 'HELD':
    case 'FAILED':
      // Delivered orders carry the date; without one, it still reads as waiting for delivery.
      if (payout.dueAt) {
        return { key: 'HELD', date: payout.dueAt, color: 'info', hintKey: 'HELD', payout };
      }
      return { key: 'AWAITING_DELIVERY', date: null, color: 'warning', hintKey: 'AWAITING_DELIVERY', payout };
    case 'ON_HOLD':
      return { key: 'ON_HOLD', date: null, color: 'error', hintKey: 'ON_HOLD', payout };
    case 'RELEASED':
      // The owner payout block carries no flags, so a cancelled order whose money was already paid
      // out (the cancel refunded the buyer from the store's balance) is recognised by the order.
      if (isCancelledOrder(order)) {
        return { key: 'REFUNDED_AFTER_RELEASE', date: null, color: 'default', hintKey: 'REFUNDED_AFTER_RELEASE', payout };
      }
      if (isPaidOutEarly(order)) {
        // Once shipped the order can no longer be cancelled, so the hint must not mention a refund.
        const hintKey = order.fulfilment?.status === 'SHIPPED' ? 'RELEASED_EARLY_SHIPPED' : 'RELEASED_EARLY';
        return { key: 'RELEASED', date: payout.releasedAt, color: 'success', hintKey, payout };
      }
      return { key: 'RELEASED', date: payout.releasedAt, color: 'success', hintKey: 'RELEASED', payout };
    case 'CANCELLED':
      // A payout cancelled by a full refund next to a "Paid" order chip reads "Refunded in full";
      // when the order itself is cancelled, "Cancelled" is the clearer word.
      return {
        key: isCancelledOrder(order) ? 'CANCELLED' : 'REFUNDED_IN_FULL',
        date: null,
        color: 'default',
        hintKey: 'CANCELLED',
        payout,
      };
    case 'AWAITING_DELIVERY':
    default:
      return { key: 'AWAITING_DELIVERY', date: null, color: 'warning', hintKey: 'AWAITING_DELIVERY', payout };
  }
}

/** Hold reasons the dashboard knows how to word; anything else is ignored rather than shown raw. */
const KNOWN_HOLD_REASONS = ['RETURN', 'DISPUTE', 'ADMIN'];

export function holdReasonKeys(payout) {
  return (payout?.holdReasons || []).filter((r) => KNOWN_HOLD_REASONS.includes(r));
}

/**
 * The store owner may cancel a STORE order while it is unpaid (CREATED: nothing to refund) or PAID
 * and not yet shipped (fulfilment AWAITING_SHIPMENT, or no fulfilment row for an order paid before
 * fulfilment tracking; the cancel refunds the buyer). Mirrors StoreCheckoutService.cancelAndRefund,
 * which answers 400 CANCEL_AFTER_SHIPPING / ORDER_NOT_CANCELLABLE otherwise.
 */
export function canCancelStoreOrder(order) {
  if (order?.orderType !== 'STORE') return false;
  if (order.status === 'CREATED') return true;
  if (order.status !== 'PAID') return false;
  const status = order.fulfilment?.status;
  return status === undefined || status === null || status === 'AWAITING_SHIPMENT';
}
